import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, FileText, Loader2, Building2, DollarSign, Trash2 } from 'lucide-react';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL || '';
const fmt = (n, c = 'PHP') => new Intl.NumberFormat('en-PH', { style: 'currency', currency: c, maximumFractionDigits: 0 }).format(n || 0);

const STATUS_TONE = {
  paid: 'bg-emerald-100 text-emerald-700',
  partial: 'bg-amber-100 text-amber-800',
  open: 'bg-gray-100 text-gray-700',
};

export default function FinanceBillsPage() {
  const [tab, setTab] = useState('bills');
  const [bills, setBills] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: b }, { data: v }, { data: s }] = await Promise.all([
        axios.get(`${API}/api/finance/bills`),
        axios.get(`${API}/api/finance/vendors`),
        axios.get(`${API}/api/finance/setup`),
      ]);
      setBills(b.items || []);
      setVendors(v.items || []);
      setTaxes(s.tax_categories || []);
    } catch {
      toast.error('Failed to load bills & vendors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const vendorName = id => vendors.find(v => v.id === id)?.name || '—';

  return (
    <div className="page-shell-wide" data-testid="finance-bills-page">
      <div className="mb-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-400 mb-1">
          <FileText className="h-3 w-3" /> Finance · Bills & Vendors
        </div>
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Bills & Vendors<PageInfoTooltip pageKey="finance-bills" /></h1>
        <p className="text-sm text-gray-600 mt-1">Track who you owe, record payments, and manage suppliers in one place.</p>
      </div>

      {loading ? (
        <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
      ) : (
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-2 max-w-md mb-4">
            <TabsTrigger value="bills" data-testid="tab-bills">Bills ({bills.length})</TabsTrigger>
            <TabsTrigger value="vendors" data-testid="tab-vendors">Vendors ({vendors.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="bills">
            <BillsTab bills={bills} vendors={vendors} taxes={taxes} vendorName={vendorName} onReload={load} />
          </TabsContent>
          <TabsContent value="vendors">
            <VendorsTab vendors={vendors} onReload={load} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function BillsTab({ bills, vendors, taxes, vendorName, onReload }) {
  const [open, setOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(null);
  const [form, setForm] = useState({ vendor_id: '', bill_number: '', issue_date: new Date().toISOString().slice(0, 10), due_date: '', amount: '', description: '', tax_category: 'VAT12_PH' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.vendor_id || !form.amount) { toast.error('Vendor and amount required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/finance/bills`, {
        vendor_id: form.vendor_id,
        bill_number: form.bill_number,
        issue_date: form.issue_date,
        due_date: form.due_date,
        tax_category: form.tax_category,
        line_items: [{ description: form.description || 'Services', amount: parseFloat(form.amount) }],
      });
      toast.success('Bill created');
      setOpen(false);
      setForm({ ...form, amount: '', description: '', bill_number: '' });
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const submitPay = async () => {
    const bill = payOpen;
    const outstanding = (bill.total || 0) - (bill.amount_paid || 0);
    setSaving(true);
    try {
      await axios.post(`${API}/api/finance/bills/${bill.id}/pay`, {
        amount: outstanding,
        method: 'bank',
      });
      toast.success('Payment recorded');
      setPayOpen(null);
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
    } finally { setSaving(false); }
  };

  return (
    <>
      <div className="flex justify-end mb-3">
        <Button onClick={() => setOpen(true)} data-testid="add-bill-btn"><Plus className="h-4 w-4 mr-1.5" />New bill</Button>
      </div>
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                <tr>
                  <th className="text-left p-2.5">Bill #</th>
                  <th className="text-left p-2.5">Vendor</th>
                  <th className="text-left p-2.5">Issued</th>
                  <th className="text-left p-2.5">Due</th>
                  <th className="text-right p-2.5">Total</th>
                  <th className="text-right p-2.5">Outstanding</th>
                  <th className="text-left p-2.5">Status</th>
                  <th className="p-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {bills.map(b => {
                  const outstanding = (b.total || 0) - (b.amount_paid || 0);
                  const overdue = b.due_date && new Date(b.due_date) < new Date() && b.status !== 'paid';
                  return (
                    <tr key={b.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`bill-row-${b.id}`}>
                      <td className="p-2.5 font-mono text-xs">{b.bill_number}</td>
                      <td className="p-2.5">{vendorName(b.vendor_id)}</td>
                      <td className="p-2.5 text-gray-500 font-mono text-xs">{b.issue_date}</td>
                      <td className={`p-2.5 font-mono text-xs ${overdue ? 'text-rose-600 font-semibold' : 'text-gray-500'}`}>{b.due_date}</td>
                      <td className="p-2.5 text-right font-mono">{fmt(b.total, b.currency)}</td>
                      <td className="p-2.5 text-right font-mono">{fmt(outstanding, b.currency)}</td>
                      <td className="p-2.5"><span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${STATUS_TONE[b.status] || STATUS_TONE.open}`}>{b.status}</span></td>
                      <td className="p-2.5 text-right">
                        {outstanding > 0 && (
                          <button onClick={() => setPayOpen(b)} className="text-xs text-blue-600 hover:underline" data-testid={`bill-pay-${b.id}`}>
                            Record payment
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {bills.length === 0 && (
                  <tr><td colSpan={8} className="p-10 text-center text-sm text-gray-400">No bills recorded.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="bill-modal">
          <DialogHeader><DialogTitle>New bill from vendor</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Vendor *</Label>
                <Select value={form.vendor_id} onValueChange={v => setForm({ ...form, vendor_id: v })}>
                  <SelectTrigger data-testid="bill-vendor"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Bill #</Label>
                <Input value={form.bill_number} onChange={e => setForm({ ...form, bill_number: e.target.value })} placeholder="Optional" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Services rendered" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Issue date</Label>
                <Input type="date" value={form.issue_date} onChange={e => setForm({ ...form, issue_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Due date</Label>
                <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Amount *</Label>
                <Input type="number" min="0" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="bill-amount" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Tax</Label>
              <Select value={form.tax_category} onValueChange={v => setForm({ ...form, tax_category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{taxes.map(t => <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="save-bill-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create bill'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payOpen} onOpenChange={(o) => !o && setPayOpen(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Record payment</DialogTitle></DialogHeader>
          {payOpen && (
            <div className="space-y-3 py-2">
              <div className="text-sm text-gray-600">Pay <strong>{fmt((payOpen.total || 0) - (payOpen.amount_paid || 0), payOpen.currency)}</strong> for bill <span className="font-mono">{payOpen.bill_number}</span></div>
              <div className="text-xs text-gray-400">Marks the bill as paid via bank transfer and updates AP aging.</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(null)}>Cancel</Button>
            <Button onClick={submitPay} disabled={saving} data-testid="confirm-pay-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Pay in full'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function VendorsTab({ vendors, onReload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', tax_id: '', category: 'Supplier', payment_terms_days: 30 });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/finance/vendors`, form);
      toast.success('Vendor added');
      setOpen(false);
      setForm({ ...form, name: '', email: '', phone: '', tax_id: '' });
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove vendor?')) return;
    try {
      await axios.delete(`${API}/api/finance/vendors/${id}`);
      toast.success('Vendor removed');
      onReload();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <>
      <div className="flex justify-end mb-3">
        <Button onClick={() => setOpen(true)} data-testid="add-vendor-btn"><Plus className="h-4 w-4 mr-1.5" />Add vendor</Button>
      </div>
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                <tr>
                  <th className="text-left p-2.5">Name</th>
                  <th className="text-left p-2.5">Category</th>
                  <th className="text-left p-2.5">Email</th>
                  <th className="text-left p-2.5">Phone</th>
                  <th className="text-right p-2.5">Terms</th>
                  <th className="p-2.5 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {vendors.map(v => (
                  <tr key={v.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`vendor-row-${v.id}`}>
                    <td className="p-2.5 font-medium">{v.name}</td>
                    <td className="p-2.5 text-gray-500">{v.category}</td>
                    <td className="p-2.5 text-gray-500 text-xs">{v.email}</td>
                    <td className="p-2.5 text-gray-500 text-xs">{v.phone}</td>
                    <td className="p-2.5 text-right text-gray-500 text-xs">{v.payment_terms_days}d</td>
                    <td className="p-2.5 text-center">
                      <button onClick={() => remove(v.id)} className="text-gray-300 hover:text-rose-600" data-testid={`vendor-delete-${v.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </td>
                  </tr>
                ))}
                {vendors.length === 0 && (
                  <tr><td colSpan={6} className="p-10 text-center text-sm text-gray-400">No vendors yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="vendor-modal">
          <DialogHeader><DialogTitle>Add vendor</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="vendor-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Email</Label><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Tax ID</Label><Input value={form.tax_id} onChange={e => setForm({ ...form, tax_id: e.target.value })} /></div>
              <div><Label className="text-xs">Payment terms (days)</Label><Input type="number" value={form.payment_terms_days} onChange={e => setForm({ ...form, payment_terms_days: parseInt(e.target.value) || 30 })} /></div>
            </div>
            <div>
              <Label className="text-xs">Category</Label>
              <Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="save-vendor-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save vendor'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
